import { useEffect, useMemo, useRef, useState } from 'react';

interface DateRangePickerProps {
  dateFrom: string;
  dateTo: string;
  onDateFromChange: (value: string) => void;
  onDateToChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  /** 可选日期跨度上限（含起止日），默认 90 天；超出时控件报错且不回写筛选值 */
  maxDays?: number;
}

const WEEKDAYS = ['日', '一', '二', '三', '四', '五', '六'];
const DEFAULT_MAX_DAYS = 90;
const DAY_MS = 24 * 60 * 60 * 1000;

function pad2(n: number) {
  return String(n).padStart(2, '0');
}

function toDateValue(d: Date) {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

function parseDateValue(value: string): Date | null {
  if (!value) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!m) return null;
  const date = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Number.isNaN(date.getTime()) ? null : date;
}

function sameDay(a: Date, b: Date) {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function startOfMonth(d: Date) {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

function addMonths(d: Date, delta: number) {
  return new Date(d.getFullYear(), d.getMonth() + delta, 1);
}

function isBetween(day: Date, from: Date | null, to: Date | null) {
  if (!from || !to) return false;
  const t = day.getTime();
  return t > from.getTime() && t < to.getTime();
}

/** 含起止日的天数跨度 */
function inclusiveDaySpan(from: Date, to: Date) {
  const a = new Date(from.getFullYear(), from.getMonth(), from.getDate()).getTime();
  const b = new Date(to.getFullYear(), to.getMonth(), to.getDate()).getTime();
  return Math.floor(Math.abs(b - a) / DAY_MS) + 1;
}

function formatRangeText(from: string, to: string) {
  if (from && to) return `${from} ~ ${to}`;
  if (from) return `${from} ~`;
  if (to) return `~ ${to}`;
  return '';
}

function buildMonthCells(viewMonth: Date) {
  const first = startOfMonth(viewMonth);
  const startOffset = first.getDay();
  const cells: Array<{ date: Date; inMonth: boolean }> = [];
  const gridStart = new Date(first);
  gridStart.setDate(first.getDate() - startOffset);

  for (let i = 0; i < 42; i++) {
    const date = new Date(gridStart);
    date.setDate(gridStart.getDate() + i);
    cells.push({
      date,
      inMonth: date.getMonth() === viewMonth.getMonth(),
    });
  }
  return cells;
}

/**
 * 日期范围选择器，样式参考 PrimeVue DatePicker（Range / Icon / Button Bar）
 * https://primevue.dev/datepicker/
 */
export default function DateRangePicker({
  dateFrom,
  dateTo,
  onDateFromChange,
  onDateToChange,
  placeholder = '选择日期范围',
  className = '',
  maxDays = DEFAULT_MAX_DAYS,
}: DateRangePickerProps) {
  const [open, setOpen] = useState(false);
  const [viewMonth, setViewMonth] = useState(() => startOfMonth(new Date()));
  const [draftFrom, setDraftFrom] = useState(dateFrom);
  const [draftTo, setDraftTo] = useState(dateTo);
  const [pickingEnd, setPickingEnd] = useState(false);
  const [error, setError] = useState('');
  const [errorFrom, setErrorFrom] = useState('');
  const [errorTo, setErrorTo] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);

  const hasValue = !!(dateFrom || dateTo || error);
  const displayText = useMemo(() => {
    if (error) return formatRangeText(errorFrom, errorTo) || placeholder;
    return formatRangeText(dateFrom, dateTo);
  }, [error, errorFrom, errorTo, dateFrom, dateTo, placeholder]);

  useEffect(() => {
    if (!open) return;
    if (error) {
      setDraftFrom(errorFrom);
      setDraftTo(errorTo);
      setPickingEnd(false);
      const base = parseDateValue(errorFrom) || parseDateValue(errorTo) || new Date();
      setViewMonth(startOfMonth(base));
      return;
    }
    setDraftFrom(dateFrom);
    setDraftTo(dateTo);
    setPickingEnd(false);
    const base = parseDateValue(dateFrom) || parseDateValue(dateTo) || new Date();
    setViewMonth(startOfMonth(base));
  }, [open, dateFrom, dateTo, error, errorFrom, errorTo]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const cells = useMemo(() => buildMonthCells(viewMonth), [viewMonth]);
  const draftFromDate = parseDateValue(draftFrom);
  const draftToDate = parseDateValue(draftTo);
  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const applyRange = (from: string, to: string) => {
    setError('');
    setErrorFrom('');
    setErrorTo('');
    onDateFromChange(from);
    onDateToChange(to);
  };

  const rejectRange = (from: string, to: string) => {
    setError(`查询范围不能超过 ${maxDays} 天`);
    setErrorFrom(from);
    setErrorTo(to);
    setDraftFrom(from);
    setDraftTo(to);
    setPickingEnd(false);
    // 不回写父级筛选值，避免触发查询
  };

  const handleDayClick = (day: Date) => {
    const value = toDateValue(day);

    if (!pickingEnd || !draftFrom) {
      setDraftFrom(value);
      setDraftTo('');
      setPickingEnd(true);
      if (error) {
        setError('');
        setErrorFrom('');
        setErrorTo('');
      }
      return;
    }

    let from = draftFrom;
    let to = value;
    if (value < draftFrom) {
      from = value;
      to = draftFrom;
    }

    const fromDate = parseDateValue(from);
    const toDate = parseDateValue(to);
    if (fromDate && toDate && inclusiveDaySpan(fromDate, toDate) > maxDays) {
      rejectRange(from, to);
      setOpen(false);
      return;
    }

    setDraftFrom(from);
    setDraftTo(to);
    setPickingEnd(false);
    applyRange(from, to);
    setOpen(false);
  };

  const handleClear = () => {
    setDraftFrom('');
    setDraftTo('');
    setPickingEnd(false);
    setError('');
    setErrorFrom('');
    setErrorTo('');
    onDateFromChange('');
    onDateToChange('');
    setOpen(false);
  };

  const handleToday = () => {
    const value = toDateValue(today);
    setDraftFrom(value);
    setDraftTo(value);
    setPickingEnd(false);
    applyRange(value, value);
    setViewMonth(startOfMonth(today));
    setOpen(false);
  };

  const monthLabel = `${viewMonth.getFullYear()}年${viewMonth.getMonth() + 1}月`;

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`w-full flex items-center gap-2 px-3 py-2 rounded-md border text-sm text-left transition-colors ${
          error
            ? 'border-amber-400 ring-2 ring-amber-100 bg-amber-50'
            : open
              ? 'border-primary-400 ring-2 ring-primary-100 bg-background-50'
              : 'border-background-200 bg-background-50 hover:border-background-300'
        }`}
      >
        <i
          className={`shrink-0 ${
            error ? 'ri-error-warning-line text-amber-500' : 'ri-calendar-line text-foreground-400'
          }`}
        />
        <span
          className={`flex-1 min-w-0 truncate ${
            displayText ? (error ? 'text-amber-800' : 'text-foreground-800') : 'text-foreground-400'
          }`}
        >
          {displayText || placeholder}
        </span>
        {hasValue ? (
          <span
            role="button"
            tabIndex={0}
            onClick={(e) => {
              e.stopPropagation();
              handleClear();
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                e.stopPropagation();
                handleClear();
              }
            }}
            className="w-5 h-5 flex items-center justify-center rounded-full text-foreground-400 hover:text-foreground-700 hover:bg-background-200 shrink-0"
            title="清除"
          >
            <i className="ri-close-circle-fill text-sm" />
          </span>
        ) : (
          <i
            className={`ri-arrow-down-s-line text-foreground-400 shrink-0 transition-transform ${
              open ? 'rotate-180' : ''
            }`}
          />
        )}
      </button>

      {error && (
        <p className="mt-1 text-[11px] text-amber-600 leading-tight flex items-center gap-1">
          <i className="ri-error-warning-line shrink-0" />
          {error}
        </p>
      )}

      {open && (
        <div className="absolute left-0 top-full mt-1 z-[80] w-[19.5rem] rounded-xl border border-background-200 bg-background-50 shadow-xl overflow-hidden">
          <div className="flex items-center justify-between px-3 py-2.5 border-b border-background-100">
            <button
              type="button"
              onClick={() => setViewMonth((m) => addMonths(m, -1))}
              className="w-8 h-8 flex items-center justify-center rounded-lg text-foreground-500 hover:bg-background-100 hover:text-foreground-800 transition-colors"
              title="上个月"
            >
              <i className="ri-arrow-left-s-line text-lg" />
            </button>
            <div className="text-sm font-semibold text-foreground-900">{monthLabel}</div>
            <button
              type="button"
              onClick={() => setViewMonth((m) => addMonths(m, 1))}
              className="w-8 h-8 flex items-center justify-center rounded-lg text-foreground-500 hover:bg-background-100 hover:text-foreground-800 transition-colors"
              title="下个月"
            >
              <i className="ri-arrow-right-s-line text-lg" />
            </button>
          </div>

          <div className="px-3 pt-2 pb-1">
            <div className="grid grid-cols-7 mb-1">
              {WEEKDAYS.map((d) => (
                <div
                  key={d}
                  className="h-8 flex items-center justify-center text-[11px] font-medium text-foreground-400"
                >
                  {d}
                </div>
              ))}
            </div>
            <div className="grid grid-cols-7 gap-y-0.5">
              {cells.map(({ date, inMonth }) => {
                const value = toDateValue(date);
                const isStart = !!draftFromDate && sameDay(date, draftFromDate);
                const isEnd = !!draftToDate && sameDay(date, draftToDate);
                const inRange = isBetween(date, draftFromDate, draftToDate);
                const isToday = sameDay(date, today);
                const isSelected = isStart || isEnd;

                return (
                  <button
                    key={value + String(inMonth)}
                    type="button"
                    onClick={() => handleDayClick(date)}
                    className={`relative h-9 flex items-center justify-center text-sm transition-colors ${
                      inRange ? 'bg-primary-50' : ''
                    } ${isStart ? 'rounded-l-full bg-primary-50' : ''} ${
                      isEnd ? 'rounded-r-full bg-primary-50' : ''
                    }`}
                  >
                    <span
                      className={`w-8 h-8 flex items-center justify-center rounded-full transition-colors ${
                        isSelected
                          ? 'bg-primary-500 text-background-50 font-semibold'
                          : inMonth
                            ? 'text-foreground-800 hover:bg-background-100'
                            : 'text-foreground-300 hover:bg-background-100'
                      } ${!isSelected && isToday ? 'ring-1 ring-primary-300 text-primary-600 font-medium' : ''}`}
                    >
                      {date.getDate()}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="px-3 py-2 border-t border-background-100 flex items-center justify-between gap-2">
            <p
              className={`text-[11px] truncate ${
                error ? 'text-amber-600' : 'text-foreground-400'
              }`}
            >
              {error
                ? error
                : pickingEnd
                  ? '请选择结束日期'
                  : draftFrom && draftTo
                    ? `${draftFrom} ~ ${draftTo}`
                    : '请选择开始日期'}
            </p>
            <div className="flex items-center gap-1.5 shrink-0">
              <button
                type="button"
                onClick={handleClear}
                className="px-2.5 py-1 rounded-md text-xs text-foreground-600 hover:bg-background-100 transition-colors"
              >
                清除
              </button>
              <button
                type="button"
                onClick={handleToday}
                className="px-2.5 py-1 rounded-md text-xs font-medium text-primary-600 hover:bg-primary-50 transition-colors"
              >
                今天
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
